import Container from '../atoms/container/mainContainer';
import Focus from '../../../shared/types/enums/focus';
import OpacityAnimation from '../atoms/others/opacityAnimation';
import Modals from '../../../shared/types/enums/modals';
import Text from '../atoms/text/emptyMainText';
import TranslationManager from '../../../shared/assets/locale/translationsManager';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import { clearModal, setModal } from '../../../shared/store/slices/modal.slice';
import { focusSelector } from '../../../shared/store/slices/focus.slice';
import { routerSelector } from '../../../shared/store/slices/router.slice';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import { userSelector } from '../../../shared/store/slices/user.slice';

const OPACITY_TIMEOUT = 300;

function TEmpty({ message, modal }: { message?: string; modal?: Modals }) {
  const dispatch = useAppDispatch();
  const { focus, stack } = useAppSelector(focusSelector);
  const { params } = useAppSelector(routerSelector);
  const { isAnonymous } = useAppSelector(userSelector);
  const [animation, setAnimation] = useState('main-content-load');
  const [displayModal, setDisplayModal] = useState(true);
  const [opacity, setOpacity] = useState(0);
  const focusRef = useRef(focus);
  const hasLoginRef = useRef<boolean | undefined>(undefined);
  const modalRef = useRef<Modals | undefined>(
    params?.current?.modal as Modals | undefined
  );
  focusRef.current = focus;
  hasLoginRef.current = isAnonymous;
  modalRef.current = modal || (params?.current?.modal as Modals | undefined);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setOpacity(1);
    }, OPACITY_TIMEOUT);

    return () => {
      clearTimeout(timeout);
      dispatch(clearModal());
    };
  }, []);

  useEffect(() => {
    if (displayModal && focus === Focus.MAIN_EMPTY && modalRef.current) {
      dispatch(setModal(modalRef.current));
      setDisplayModal(false);
    }
    if (focus === Focus.MENU) {
      setDisplayModal(true);
    }
  }, [displayModal, focus]);

  /* Set animation based on focus */
  useEffect(() => {
    setAnimation(
      focus === Focus.MENU || stack.includes(Focus.MENU)
        ? 'main-content-out'
        : 'main-content-in'
    );
  }, [focus, stack]);

  useBackKeyboard();

  useKeyboard({
    canBeFocused: false,
    keyDownHandler(key) {
      switch (key) {
        case 'RIGHT':
        case 'SELECT':
          if (modalRef.current) {
            dispatch(setModal(modalRef.current));
          }
          break;
      }
    },
  });

  return (
    <>
      <Container>
        <OpacityAnimation
          animation={{ transition: 'opacity .2s' }}
          opacity={opacity}
        >
          <Text
            animation={animation}
            force={true}
            dangerouslySetInnerHTML={{
              __html:
                message ||
                ((params?.current?.message as string) ??
                  TranslationManager.get('PAGES.EMPTY.NO_RESULTS')),
            }}
          />
        </OpacityAnimation>
      </Container>
    </>
  );
}

export default TEmpty;
